import { useRef, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { BlockImportCommitResponse, BlockImportInput, BlockImportPreview, MeResponse } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { Link } from "../../router/Router";
import { levelLabel } from "./BlockCard";
import { SeasonPicker } from "./SeasonPicker";
import { useBlockSeason } from "./useBlockSeason";
import {
  IMPORT_MAX_BYTES,
  IMPORT_TEMPLATE_HREF,
  importErrorCount,
  importNewUsersText,
  importRowPath,
  importSummaryText
} from "./blockImport";

interface CSVFile {
  name: string;
  size: number;
  text: string;
}

type Phase =
  | { kind: "editing" }
  | { kind: "previewing" }
  | { kind: "previewed"; input: BlockImportInput; preview: BlockImportPreview }
  | { kind: "committing"; input: BlockImportInput; preview: BlockImportPreview }
  | { kind: "done"; result: BlockImportCommitResponse };

// Mass block upload (leadership only): pick a season and a default due date,
// choose a CSV, preview it — a dry run listing every problem — then commit,
// which imports every row or none.
export function BlockImportPage({ me }: { me: MeResponse }) {
  const { ready: seasonReady, seasonId, seasons, setSeasonId } = useBlockSeason(me, ["draft", "active"]);
  const [defaultDueDate, setDefaultDueDate] = useState("");
  const [createMissingUsers, setCreateMissingUsers] = useState(false);
  const [file, setFile] = useState<CSVFile | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "editing" });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();
  // Bumped by every edit and preview, so a response that lands after either
  // is dropped instead of offering Commit for inputs that are gone.
  const latestRequest = useRef(0);

  // A preview only vouches for the exact inputs it checked, so any edit sends
  // the user back through Preview before Commit is offered again.
  function edited() {
    latestRequest.current += 1;
    setPhase({ kind: "editing" });
    setErrorMessage(null);
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    // Clearing the input lets the same file be re-chosen after it is fixed.
    event.target.value = "";
    if (chosen === undefined) {
      return;
    }
    edited();
    // Dropped up front so the previous file cannot be previewed while this one is read.
    setFile(null);
    if (chosen.size > IMPORT_MAX_BYTES) {
      setErrorMessage(`"${chosen.name}" is larger than 1 MB.`);
      return;
    }
    const bytes = await chosen.arrayBuffer();
    let text: string;
    try {
      // fatal: chosen.text() would silently turn Excel's Windows-1252 "CSV"
      // into U+FFFD instead of failing.
      text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
      setErrorMessage("This file isn't UTF-8 — in Excel use Save As → CSV UTF-8.");
      return;
    }
    setFile({ name: chosen.name, size: chosen.size, text });
  }

  function payload(csvFile: CSVFile): BlockImportInput {
    return {
      csv: csvFile.text,
      default_due_date: defaultDueDate,
      season_id: seasonId,
      create_missing_users: createMissingUsers
    };
  }

  async function handlePreview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (file === null || defaultDueDate === "") {
      return;
    }
    const input = payload(file);
    const request = ++latestRequest.current;
    reset();
    setPhase({ kind: "previewing" });
    setErrorMessage(null);
    try {
      const preview = await apiClient.previewBlockImport(input);
      if (request === latestRequest.current) {
        setPhase({ kind: "previewed", input, preview });
      }
    } catch (caught) {
      if (request === latestRequest.current) {
        setPhase({ kind: "editing" });
        setErrorMessage(messageFor(caught));
      }
    }
  }

  async function handleCommit() {
    if (phase.kind !== "previewed") {
      return;
    }
    const { input, preview } = phase;
    const request = latestRequest.current;
    setPhase({ kind: "committing", input, preview });
    setErrorMessage(null);
    try {
      const result = await apiClient.commitBlockImport(input, keyFor(JSON.stringify(input)));
      reset();
      setPhase({ kind: "done", result });
    } catch (caught) {
      // The server stores every answer under the key and replays it, so a
      // refused commit needs a new key to be retried; a network error may never
      // have reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      if (request !== latestRequest.current) {
        return;
      }
      // A refused commit carries a fresh preview (the season changed under us,
      // say); show it so the new problems are visible.
      const fresh = caught instanceof ApiError ? caught.body?.preview : undefined;
      setPhase({ kind: "previewed", input, preview: fresh ?? preview });
      setErrorMessage(messageFor(caught));
    }
  }

  function startOver() {
    setFile(null);
    edited();
  }

  if (phase.kind === "done") {
    return (
      <div className="blocks-view">
        <section className="card">
          <h2>Import complete</h2>
          <p className="success">
            Imported {importSummaryText(phase.result.summary)} into {phase.result.season_name}.
          </p>
          <p className="muted">
            No assignment emails were sent. Pick {phase.result.season_name} in the season picker on All Blocks to
            see the new blocks.
          </p>
          <div className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={startOver}>
              Upload another file
            </button>
            <Link to="/blocks/all" className="btn btn--primary">
              Go to All Blocks
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const preview = phase.kind === "previewed" || phase.kind === "committing" ? phase.preview : null;
  const errorCount = preview === null ? 0 : importErrorCount(preview);

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>Mass block upload</h2>
        <p className="muted">
          Create Projects, Blocks and Sub-blocks in bulk from a CSV. Preview checks every row and writes nothing;
          Commit imports all rows or none, and never changes existing blocks.{" "}
          <a href={IMPORT_TEMPLATE_HREF} download="blocks-import-template.csv">
            Download the template
          </a>
          .
        </p>
        <details className="form-row__hint">
          <summary>Column rules</summary>
          <ul className="upload-help__list">
            <li>
              <strong>Project / Block / Sub-block</strong> — which names are filled sets the level. Every Block and
              Sub-block needs its parent's own row in the same file (any order). Projects already in the season are
              rejected.
            </li>
            <li>
              <strong>Pillar</strong> — required on Project rows; on other rows leave it blank or repeat the
              Project's.
            </li>
            <li>
              <strong>Impact, Significance</strong> — Project rows only, 1–5, both or neither (neither leaves it
              unsized). <strong>Size</strong> — Block and Sub-block rows only.
            </li>
            <li>
              <strong>Emails</strong> — must belong to members, unless <em>Create missing users</em> is checked.
              Contributor Emails are separated by <code>;</code> and split credit evenly with the Assignee.
            </li>
            <li>
              <strong>Due Date</strong> — <code>YYYY-MM-DD</code>; blank uses the default below.{" "}
              <strong>Progress</strong> — whole number; 100 or more marks the block complete.
            </li>
          </ul>
        </details>

        <form onSubmit={handlePreview}>
          <div className="form-row form-row--inline">
            <SeasonPicker
              seasons={seasons}
              value={seasonId}
              onChange={(value) => {
                setSeasonId(value);
                edited();
              }}
            />
            <label>
              Default due date
              <input
                type="date"
                value={defaultDueDate}
                required
                onChange={(event) => {
                  setDefaultDueDate(event.target.value);
                  edited();
                }}
              />
            </label>
            {hasPermission(me, "members:manage") && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={createMissingUsers}
                  onChange={(event) => {
                    setCreateMissingUsers(event.target.checked);
                    edited();
                  }}
                />
                Create missing users
              </label>
            )}
          </div>
          <label className={`dropzone${file !== null ? " has-file" : ""}`}>
            <input type="file" accept=".csv,text/csv" className="dropzone__input" onChange={(event) => void handleFile(event)} />
            {file === null ? (
              <div className="dropzone__prompt">
                <strong>Choose a CSV file</strong>
                <span className="muted">Up to 1 MB and 1,000 rows</span>
              </div>
            ) : (
              <div className="dropzone__file">
                <div className="dropzone__file-text">
                  <strong>{file.name}</strong>
                  <span>{formatKB(file.size)}</span>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost dropzone__clear"
                  onClick={(event) => {
                    // Keep the click from reaching the label and reopening the picker.
                    event.preventDefault();
                    event.stopPropagation();
                    startOver();
                  }}
                >
                  Replace
                </button>
              </div>
            )}
          </label>
          {errorMessage !== null && <p className="form-row__error">{errorMessage}</p>}
          <div className="modal__footer">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                !seasonReady ||
                file === null ||
                defaultDueDate === "" ||
                phase.kind === "previewing" ||
                phase.kind === "committing"
              }
            >
              {phase.kind === "previewing" ? "Checking…" : "Preview"}
            </button>
          </div>
        </form>
      </section>

      {preview !== null && (
        <section className="card">
          <h2>Preview</h2>
          <p>
            Into <strong>{preview.season_name}</strong>: {importSummaryText(preview.summary)}.
          </p>
          {errorCount > 0 ? (
            <p className="form-row__error">
              {errorCount} problem{errorCount === 1 ? "" : "s"} found. Fix the file and preview again — nothing has
              been imported.
            </p>
          ) : (
            <p className="success">No problems found.</p>
          )}
          {preview.errors.length > 0 && (
            <ul className="import-table__errors">
              {preview.errors.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          )}
          {preview.new_users.length > 0 && <p>{importNewUsersText(preview.new_users)}</p>}
          {preview.rows.length > 0 && (
            <div className="import-table">
              <p className="muted">Line numbers match the CSV file; the header is line 1.</p>
              <table className="ledger-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Level</th>
                    <th>Path</th>
                    <th>Pillar</th>
                    <th>Team</th>
                    <th>Owner</th>
                    <th>Assignee</th>
                    <th>Contributors</th>
                    <th>Size</th>
                    <th>Due</th>
                    <th>Progress</th>
                    <th>Problems</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.line} className={row.errors.length > 0 ? "import-table__row--error" : undefined}>
                      <td>{row.line}</td>
                      <td>{row.level === "" ? "—" : levelLabel(row.level)}</td>
                      <td className="import-table__path">{importRowPath(row)}</td>
                      <td>{row.pillar}</td>
                      <td>{row.team}</td>
                      <td>{row.exec_owner_email}</td>
                      <td>{row.assignee_email}</td>
                      <td>{row.contributor_emails.join(", ")}</td>
                      <td>{row.size}</td>
                      <td>{row.due_date}</td>
                      <td>{row.progress}</td>
                      <td>
                        {row.errors.length > 0 && (
                          <ul className="import-table__errors">
                            {row.errors.map((message, index) => (
                              <li key={index}>{message}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--primary"
              disabled={errorCount > 0 || phase.kind === "committing"}
              onClick={() => void handleCommit()}
            >
              {phase.kind === "committing" ? "Importing…" : "Commit import"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function formatKB(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Upload failed.";
}
